import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Gift, CheckCircle, XCircle, Loader2, Sparkles } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';

const GiftTrialPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  
  const [giftInfo, setGiftInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchGiftInfo = async () => {
      try {
        const { data, error } = await apiFetch(`/api/friends/gift-trial/${token}`);
        if (error) {
          setError(error || 'Invalid or expired gift link');
        } else {
          setGiftInfo(data);
        }
      } catch (err) {
        setError('Failed to load gift information');
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchGiftInfo();
    }
  }, [token]);

  const handleRedeem = async () => {
    if (!isAuthenticated) {
      // Redirect to register with return URL
      navigate(`/register?redirect=/gift/${token}`);
      return;
    }

    setIsRedeeming(true);
    try {
      const { data, error } = await apiFetch(`/api/friends/redeem-gift/${token}`, {
        method: 'POST'
      });

      if (error) {
        toast.error(error || 'Failed to redeem gift');
      } else {
        setSuccess(true);
        toast.success(data.message);
        setTimeout(() => navigate('/'), 3000);
      }
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setIsRedeeming(false);
    }
  };

  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-12 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-12">
          <div className="max-w-md mx-auto">
            <Card>
              <CardHeader className="text-center">
                <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
                <CardTitle className="text-2xl">Invalid Gift</CardTitle>
                <CardDescription>{error}</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button onClick={() => navigate('/')}>Go Home</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-12">
          <div className="max-w-md mx-auto">
            <Card className="border-green-200">
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Sparkles className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-2xl text-green-700">Gift Redeemed!</CardTitle>
                <CardDescription>
                  Enjoy your 2-week free trial! You've also been added as friends with {giftInfo?.gifter_name}.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground mb-4">Redirecting you to the app...</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container py-12">
        <div className="max-w-md mx-auto">
          <Card className="border-purple-200">
            <CardHeader className="text-center bg-gradient-to-b from-purple-50 to-white rounded-t-lg">
              <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <Gift className="h-8 w-8 text-purple-600" />
              </div>
              <CardTitle className="text-2xl">You've Got a Gift!</CardTitle>
              <CardDescription>
                <strong>{giftInfo?.gifter_name}</strong> is giving you a <strong>{giftInfo?.trial_days}-day free trial</strong> of ClearCommunicationApp!
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-6">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                <h3 className="font-medium mb-2 text-purple-900">What you'll get:</h3>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li>• Full access to 3,400+ phrases</li>
                  <li>• Understand implied meanings in communication</li>
                  <li>• AI-powered communication decoder</li>
                  <li>• Medical terminology library</li>
                  <li>• Automatic friendship with {giftInfo?.gifter_name}</li>
                </ul>
              </div>
              
              <div className="text-center text-sm text-muted-foreground">
                <p>No credit card required for this trial!</p>
              </div>
              
              {isAuthenticated ? (
                <Button 
                  onClick={handleRedeem} 
                  className="w-full bg-purple-600 hover:bg-purple-700" 
                  disabled={isRedeeming}
                >
                  {isRedeeming ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Redeeming...
                    </>
                  ) : (
                    <>
                      <Gift className="h-4 w-4 mr-2" />
                      Claim My Free Trial
                    </>
                  )}
                </Button>
              ) : (
                <div className="space-y-2">
                  <Button onClick={handleRedeem} className="w-full bg-purple-600 hover:bg-purple-700">
                    <Gift className="h-4 w-4 mr-2" />
                    Create Account & Claim Gift
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    Already have an account? <a href={`/login?redirect=/gift/${token}`} className="text-purple-600 hover:underline">Sign in</a>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GiftTrialPage;
